import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Checkbox,
  Avatar,
  Chip,
  Box,
  Typography,
  CircularProgress,
  Divider,
  Tab,
  Tabs,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import ArticleIcon from '@mui/icons-material/Article';

import { useTranslation } from 'react-i18next';
import { useAppStore } from '../../stores/appStore';
import { useUserGroupStore } from '../../stores/userGroupStore';
import { useUserManager } from '../../hooks/managers/useUserManager';
import { getFriends } from '../../hooks/api/friendshipAPI';
import { useGlobalWebSocket } from '../../hooks/useGlobalWebSocket';
import { useMessageStore } from '../../stores/messageStore';
import {
  ChatMessage,
  MessageType,
  generateTempId,
  getConversationId,
  getGroupConversationId,
} from '../../types/message';
import { WSMessageType } from '../../types/websocket';
import { useSnackbar } from 'notistack';

interface ShareTarget {
  id: string;
  name: string;
  subtitle: string;
  type: 'friend' | 'group';
  avatarPath?: string;
}

interface ShareMemoDialogProps {
  open: boolean;
  onClose: () => void;
  memoTitle: string;
  memoUrl: string;
  onShareSuccess?: () => void;
}

export const ShareMemoDialog: React.FC<ShareMemoDialogProps> = ({
  open,
  onClose,
  memoTitle,
  memoUrl,
  onShareSuccess,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useUserManager();

  const friends = useAppStore((state) => state.friends);
  const setFriends = useAppStore((state) => state.setFriends);
  const userInfoMap = useAppStore((state) => state.userInfoMap);
  const userGroups = useUserGroupStore((state) => state.userGroups);

  const {
    sendRequest,
    isConnected,
    createPrivateConversationViaWS,
    createGroupConversationViaWS,
  } = useGlobalWebSocket();

  const addMessage = useMessageStore((state) => state.addMessage);
  const addConversation = useMessageStore((state) => state.addConversation);
  const updateMessageStatus = useMessageStore((state) => state.updateMessageStatus);

  const [searchQuery, setSearchQuery] = useState('');
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState(false);

  // 加载好友列表
  useEffect(() => {
    if (!open) return;
    if (friends && friends.length > 0) return;
    let cancelled = false;
    getFriends()
      .then((data) => {
        if (cancelled) return;
        if (Array.isArray(data) && data.length > 0) {
          setFriends(data);
        }
      })
      .catch((err) => {
        console.warn('ShareMemoDialog 加载 friends 失败:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [open, friends, setFriends]);

  // 构建好友列表
  const friendTargets: ShareTarget[] = useMemo(() => {
    if (!friends) return [];
    return friends.map((friend: any) => {
      const id = String(friend.friendId || friend.userId);
      const cached = userInfoMap[id];
      const username = cached?.username || '';
      const name =
        cached?.nickName || username || friend?.remark || `User ${id}`;
      const subtitleParts: string[] = [];
      if (username) subtitleParts.push(`@${username}`);
      subtitleParts.push(`ID: ${id}`);
      return {
        id,
        name,
        subtitle: subtitleParts.join('  '),
        type: 'friend' as const,
        avatarPath: cached?.avatarPath,
      };
    });
  }, [friends, userInfoMap]);

  // 构建群组列表
  const groupTargets: ShareTarget[] = useMemo(() => {
    if (!userGroups) return [];
    return userGroups
      .flatMap((ug: any) => {
        const id = String(ug?.groupId ?? ug?.id ?? '');
        const name = ug?.groupName || '';
        if (!name) return [];
        return [{
          id,
          name,
          subtitle: `ID: ${id}`,
          type: 'group' as const,
          avatarPath: ug?.avatarPath,
        }];
      });
  }, [userGroups]);

  // 搜索过滤
  const filteredFriends = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return friendTargets;
    return friendTargets.filter(
      (f) =>
        f.name.toLowerCase().includes(q) ||
        f.subtitle.toLowerCase().includes(q)
    );
  }, [friendTargets, searchQuery]);

  const filteredGroups = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return groupTargets;
    return groupTargets.filter(
      (g) =>
        g.name.toLowerCase().includes(q) ||
        g.subtitle.toLowerCase().includes(q)
    );
  }, [groupTargets, searchQuery]);

  const handleToggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleShare = useCallback(async () => {
    if (selectedIds.size === 0) return;
    if (!currentUser?.userId) return;
    if (!isConnected()) {
      enqueueSnackbar(t('errorMessageWebSocketNotConnected'), { variant: 'error' });
      return;
    }

    setSending(true);
    const cardContent = JSON.stringify({
      type: 'memo_card',
      title: memoTitle,
      url: memoUrl,
    });

    // Use TEXT type so cross-chat can identify memo_card via content prefix
    const memoCardMessageType = MessageType.TEXT;

    const selectedFriends = friendTargets.filter((f) => selectedIds.has(f.id));
    const selectedGroups = groupTargets.filter((g) => selectedIds.has(g.id));
    let successCount = 0;
    let failCount = 0;

    // 发送给好友
    for (const friend of selectedFriends) {
      const conversationId = getConversationId(
        String(currentUser.userId),
        friend.id
      );
      const tempId = generateTempId();

      try {
        await createPrivateConversationViaWS(friend.id);
      } catch (e) {
        // 会话可能已存在
      }

      const tempMessage: ChatMessage = {
        id: tempId,
        conversationId,
        senderId: String(currentUser.userId),
        receiverId: friend.id,
        content: cardContent,
        messageType: memoCardMessageType,
        timestamp: Date.now(),
        status: 'SENDING',
        isSelf: true,
      };
      addMessage(conversationId, tempMessage);

      try {
        await sendRequest(
          WSMessageType.CHAT,
          {
            conversationId,
            senderId: String(currentUser.userId),
            receiverId: friend.id,
            content: cardContent,
            messageType: memoCardMessageType,
            clientMessageId: tempId,
          },
          10000
        );
        updateMessageStatus(conversationId, tempId, 'SENT');
        successCount++;
      } catch (e) {
        updateMessageStatus(conversationId, tempId, 'FAILED');
        failCount++;
      }
    }

    // 发送给群组
    for (const group of selectedGroups) {
      const conversationId = getGroupConversationId(group.id);
      const tempId = generateTempId();

      try {
        await createGroupConversationViaWS(
          Number(group.id),
          group.name,
          group.avatarPath
        );
      } catch (e) {
        // 会话可能已存在
      }

      const tempMessage: ChatMessage = {
        id: tempId,
        conversationId,
        senderId: String(currentUser.userId),
        receiverId: group.id,
        content: cardContent,
        messageType: memoCardMessageType,
        timestamp: Date.now(),
        status: 'SENDING',
        isSelf: true,
      };
      addMessage(conversationId, tempMessage);

      try {
        await sendRequest(
          WSMessageType.GROUP_MESSAGE,
          {
            conversationId,
            senderId: String(currentUser.userId),
            receiverId: group.id,
            content: cardContent,
            messageType: memoCardMessageType,
            clientMessageId: tempId,
          },
          10000
        );
        updateMessageStatus(conversationId, tempId, 'SENT');
        successCount++;
      } catch (e) {
        updateMessageStatus(conversationId, tempId, 'FAILED');
        failCount++;
      }
    }

    setSending(false);

    if (successCount > 0) {
      enqueueSnackbar(
        t('shareMemoSuccess', { count: successCount }),
        { variant: 'success' }
      );
    }
    if (failCount > 0 && successCount === 0) {
      enqueueSnackbar(t('shareMemoFailed'), { variant: 'error' });
    }

    setSelectedIds(new Set());
    onClose();
    if (successCount > 0 && onShareSuccess) {
      onShareSuccess();
    }
  }, [
    selectedIds,
    currentUser,
    isConnected,
    memoTitle,
    memoUrl,
    friendTargets,
    groupTargets,
    createPrivateConversationViaWS,
    createGroupConversationViaWS,
    sendRequest,
    addMessage,
    updateMessageStatus,
    enqueueSnackbar,
    t,
    onClose,
    onShareSuccess,
  ]);

  const handleClose = useCallback(() => {
    if (sending) return;
    setSelectedIds(new Set());
    setSearchQuery('');
    setTabIndex(0);
    onClose();
  }, [sending, onClose]);

  const totalSelected = selectedIds.size;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ArticleIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6">{t('shareMemoTitle')}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {memoTitle}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* 搜索框 */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t('shareMemoSearchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Divider />

        {/* Tab 切换 */}
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          variant="fullWidth"
        >
          <Tab
            label={`${t('shareMemoTabFriends')} (${filteredFriends.length})`}
          />
          <Tab
            label={`${t('shareMemoTabGroups')} (${filteredGroups.length})`}
          />
        </Tabs>

        <Divider />

        {/* 列表 */}
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {tabIndex === 0 && (
            <ShareTargetList
              targets={filteredFriends}
              selectedIds={selectedIds}
              onToggle={handleToggle}
              emptyText={
                searchQuery ? t('shareMemoNoResults') : t('shareMemoNoFriends')
              }
              iconType="friend"
            />
          )}
          {tabIndex === 1 && (
            <ShareTargetList
              targets={filteredGroups}
              selectedIds={selectedIds}
              onToggle={handleToggle}
              emptyText={
                searchQuery ? t('shareMemoNoResults') : t('shareMemoNoGroups')
              }
              iconType="group"
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, justifyContent: 'space-between' }}>
        <Chip
          size="small"
          label={t('shareMemoSelected', { count: totalSelected })}
          color={totalSelected > 0 ? 'primary' : 'default'}
          variant={totalSelected > 0 ? 'filled' : 'outlined'}
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleClose} disabled={sending}>
            {t('buttonCancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleShare}
            disabled={totalSelected === 0 || sending}
            startIcon={sending ? <CircularProgress size={16} /> : undefined}
          >
            {sending
              ? t('shareMemoSending')
              : `${t('shareMemoButtonShare')} (${totalSelected})`}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

// 分享目标列表子组件
const ShareTargetList: React.FC<{
  targets: ShareTarget[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  emptyText: string;
  iconType: 'friend' | 'group';
}> = ({ targets, selectedIds, onToggle, emptyText, iconType }) => {
  if (targets.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <Typography color="text.secondary">{emptyText}</Typography>
      </Box>
    );
  }

  return (
    <List dense>
      {targets.map((target) => {
        const isSelected = selectedIds.has(target.id);
        return (
          <ListItem key={target.id} disablePadding>
            <ListItemButton
              onClick={() => onToggle(target.id)}
              selected={isSelected}
              dense
            >
              <Checkbox
                edge="start"
                checked={isSelected}
                tabIndex={-1}
                size="small"
                sx={{ p: 0.5 }}
              />
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: iconType === 'friend' ? 'primary.main' : 'secondary.main',
                    fontSize: '0.875rem',
                  }}
                >
                  {iconType === 'friend' ? (
                    <PersonIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <GroupIcon sx={{ fontSize: 18 }} />
                  )}
                </Avatar>
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" noWrap>
                      {target.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={iconType === 'friend' ? '好友' : '群组'}
                      sx={{
                        height: 16,
                        fontSize: '0.625rem',
                        bgcolor:
                          iconType === 'friend'
                            ? 'primary.light'
                            : 'secondary.light',
                        color: 'white',
                      }}
                    />
                  </Box>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {target.subtitle}
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
};
